// src/contexts/AuthContext.tsx
import React, { createContext, useState, useContext, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from "react-router-dom";
import type { User } from 'jasit-api-kit';
import type { AuthTokens } from '@/lib/models/auth';
import type { CandidateProfileCreatePayload } from '@/lib/models/candidateProfile';
import { ApiError, toCompatError } from '@/lib/api/apiUtils';
import { friendlyAuthError } from '@/lib/api/authErrors';
import { getApiClient, getTokenManager, getAuthApi, getUserApi } from '@/lib/api/client';
import { useToast } from '@/hooks/use-toast';

interface AuthContextType {
    user: User | null;
    isAuthenticated: boolean;
    tokens: AuthTokens | null;
    isLoading: boolean;
    login: (email: string, password: string) => Promise<{ success: boolean, error?: ApiError, user?: User }>;
    initiateGoogleLogin: (redirectPath?: string, role?: string) => Promise<{ success: boolean, googleUrl?: string, error?: ApiError }>;
    handleGoogleCallback: (code: string, redirectUrl: string, role?: string, extra?: any) => Promise<{ success: boolean, error?: ApiError }>;
    register: (
        email: string,
        password: string,
        role: 'candidate' | 'employer' | 'admin',
        firstName?: string,
        lastName?: string,
        candidateProfile?: CandidateProfileCreatePayload
        // `isVerified` is the API's answer, not a guess — see register().
    ) => Promise<{ success: boolean, error?: ApiError, isVerified?: boolean }>;
    verifyEmail: (token: string, email?: string | null) => Promise<{ success: boolean, error?: ApiError }>;
    getCurrentUser: (forceRefresh: boolean) => Promise<User | null>;
    logout: (redirectTo?: string) => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [user, setUser] = useState<User | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const navigate = useNavigate();
    const { toast } = useToast();

    const tokenManager = getTokenManager();
    const authApi = getAuthApi();
    const userApi = getUserApi();

    // #308 — expiry is ONE event, however many requests discover it.
    //
    // The toast used to fire per detection, so anything polling in the background
    // produced a fresh "Session Expired" every few seconds on the login screen
    // (the Link status poll, every 4s, indefinitely). The poller itself is fixed
    // where it lives, but this guard is what stops the NEXT background poll
    // someone adds from reintroducing the same behaviour: telling the user their
    // session expired is worth doing exactly once.
    //
    // Reset on any successful sign-in, so a later expiry notifies again.
    const sessionExpiredNotifiedRef = useRef(false);
    const notifySessionExpired = () => {
        setUser(null);
        if (sessionExpiredNotifiedRef.current) return;
        sessionExpiredNotifiedRef.current = true;
        toast({
            title: "Sessionen har gått ut",
            description: "Logga in igen för att fortsätta.",
            variant: "destructive",
            duration: 3000,
        });
    };
    /** Call on every successful authentication so a FUTURE expiry can notify. */
    const markSignedIn = (u: User | null) => {
        if (u) sessionExpiredNotifiedRef.current = false;
        setUser(u);
    };

    /**
     * Turn a thrown API error into an ApiError whose `message` is Swedish and
     * actionable, and toast it.
     *
     * Use this only where NOTHING else shows the failure. Login and signup both
     * render the error inside their own form, and toasting there produced a
     * second, worse copy of it: a red "Error (400)" carrying the API's English
     * sentence. The map in authErrors.ts is the single source of that copy.
     */
    const authError = (error: unknown, opts: { toast: boolean } = { toast: true }): ApiError => {
        const compatError = toCompatError(error);
        const friendly = friendlyAuthError(compatError);
        // Overwrite the API's English message so a caller that renders
        // `error.message` inline gets the Swedish copy without knowing about
        // this module.
        compatError.message = friendly.description;
        if (opts.toast) {
            toast({
                title: friendly.title,
                description: friendly.description,
                variant: "destructive",
                duration: 5000,
            });
        }
        return compatError;
    };

    // Wire session-expired callback
    useEffect(() => {
        getApiClient().setOnSessionExpired(() => {
            notifySessionExpired();
            navigate('/');
        });
    }, []);

    // Check for existing session on mount
    useEffect(() => {
        const initializeAuth = async () => {
            const tokens = tokenManager.getTokens();
            if (!tokens) {
                setIsLoading(false);
                return;
            }

            try {
                // If access token expired, try refreshing
                if (tokenManager.isTokenExpired(tokens.access_token)) {
                    const newTokens = await authApi.refreshToken(tokens.refresh_token);
                    tokenManager.saveTokens(newTokens);
                }

                const currentUser = await userApi.getCurrentUser();
                markSignedIn(currentUser);
                // (No "Welcome back" toast on session restore — it fired on every
                // load and carried no useful signal.)
            } catch {
                // Token refresh or fetch failed — clear stale tokens silently
                tokenManager.clearTokens();
            } finally {
                setIsLoading(false);
            }
        };

        initializeAuth();
    }, []);

    const login = async (email: string, password: string): Promise<{ success: boolean, error?: ApiError, user?: User }> => {
        try {
            setIsLoading(true);
            const tokens = await authApi.login({ identifier: email, password });
            tokenManager.saveTokens(tokens);

            const loggedInUser = await userApi.getCurrentUser();
            markSignedIn(loggedInUser);

            // Success toast intentionally not shown here — caller decides
            // (allows role-gating to suppress the welcome on disallowed accounts).
            return { success: true, user: loggedInUser };
        } catch (error) {
            // No toast: LoginForm renders this inline, and two surfaces for one
            // failure is what made the old red "Error (401)" so noisy.
            const processedError = authError(error, { toast: false });
            return { success: false, error: processedError };
        } finally {
            setIsLoading(false);
        }
    };

    const register = async (
        email: string,
        password: string,
        role: 'candidate' | 'employer' | 'admin',
        firstName?: string,
        lastName?: string,
        candidateProfile?: CandidateProfileCreatePayload
    ): Promise<{ success: boolean, error?: ApiError, isVerified?: boolean }> => {
        try {
            setIsLoading(true);
            const created = await authApi.signup({
                email,
                password,
                role,
                first_name: firstName,
                last_name: lastName,
                ...(candidateProfile && { extra: candidateProfile as unknown as Record<string, unknown> })
            });

            // No toast on success either: every caller shows its own confirmation
            // (the celebration view, or Login's "Registrering lyckades"), and the
            // old English one stacked a third message on top of those.
            //
            // isVerified comes from the API rather than an assumption: with
            // APP_AUTO_VERIFY on, the account is live immediately and no email is
            // sent, so "kolla din e-post" would send the user hunting for a mail
            // that does not exist.
            return { success: true, isVerified: created?.is_verified ?? false };
        } catch (error) {
            // No toast: the signup forms render this inline, next to the fields.
            const processedError = authError(error, { toast: false });
            return { success: false, error: processedError };
        } finally {
            setIsLoading(false);
        }
    };

    const initiateGoogleLogin = async (
        redirectPath: string = '/auth/callback',
        role: string = 'candidate'
    ): Promise<{ success: boolean, googleUrl?: string, error?: ApiError }> => {
        try {
            setIsLoading(true);
            const redirectUrl = `${window.location.origin}${redirectPath}`;
            const googleUrl = await authApi.initiateOAuth('google', redirectUrl, role);
            return { success: true, googleUrl };
        } catch (error) {
            const processedError = authError(error);
            return { success: false, error: processedError };
        } finally {
            setIsLoading(false);
        }
    };

    const handleGoogleCallback = async (
        code: string,
        redirectUrl: string,
        role?: string,
        extra?: any
    ): Promise<{ success: boolean, error?: ApiError }> => {
        try {
            setIsLoading(true);
            const tokens = await authApi.handleOAuthCallback('google', {
                code,
                redirect_url: redirectUrl,
                role: role || 'candidate',
                ...(extra && { extra }),
            });
            tokenManager.saveTokens(tokens);

            const loggedInUser = await userApi.getCurrentUser();
            markSignedIn(loggedInUser);

            toast({
                title: "Login successful",
                description: `Welcome, ${loggedInUser.first_name}!`,
                duration: 3000,
            });

            return { success: true };
        } catch (error) {
            const processedError = authError(error);
            return { success: false, error: processedError };
        } finally {
            setIsLoading(false);
        }
    };

    const verifyEmail = async (token: string, email?: string | null): Promise<{ success: boolean, error?: ApiError }> => {
        try {
            setIsLoading(true);
            const tokens = await authApi.verifyEmail({ token, email: email ?? undefined });
            tokenManager.saveTokens(tokens);

            const verifiedUser = await userApi.getCurrentUser();
            markSignedIn(verifiedUser);

            toast({
                title: "E-postadressen är bekräftad",
                description: "Ditt konto är aktivt.",
                duration: 5000,
            });

            return { success: true };
        } catch (error) {
            const processedError = authError(error);
            return { success: false, error: processedError };
        } finally {
            setIsLoading(false);
        }
    };

    const getCurrentUser = async (forceRefresh: boolean = false): Promise<User | null> => {
        try {
            setIsLoading(true);

            if (forceRefresh || !tokenManager.getTokens()) {
                // Try refresh
                const existingTokens = tokenManager.getTokens();
                if (!existingTokens?.refresh_token) {
                    notifySessionExpired();
                    navigate('/');
                    return null;
                }
            }

            const currentUser = await userApi.getCurrentUser();
            markSignedIn(currentUser);
            return currentUser;
        } catch {
            tokenManager.clearTokens();
            notifySessionExpired();
            navigate('/');
            return null;
        } finally {
            setIsLoading(false);
        }
    };

    const logout = (redirectTo: string = '/'): void => {
        tokenManager.clearTokens();
        setUser(null);

        toast({
            title: "Du är utloggad",
            description: "Välkommen tillbaka när du vill.",
            duration: 3000,
        });

        if (redirectTo) {
            navigate(redirectTo);
        }
    };

    // Memoize tokens to prevent re-creating object on every render
    const tokens = useMemo(() => tokenManager.getTokens(), [user?.id]);

    // Memoize context value to prevent unnecessary re-renders of consumers
    const value = useMemo(() => ({
        user,
        isAuthenticated: !!user,
        tokens,
        isLoading,
        login,
        register,
        initiateGoogleLogin,
        handleGoogleCallback,
        verifyEmail,
        getCurrentUser,
        logout,
    }), [user, tokens, isLoading]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = (): AuthContextType => {
    const context = useContext(AuthContext);
    if (context === undefined) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};
